import React, { FC, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { NavHashLink } from 'react-router-hash-link'

interface Prop {
  open: boolean
  onClose: () => void
}

const SYLLABUS_URL =
  'https://docs.google.com/document/d/16Cvxv-A0cWn821JXLTZbJ_E6qkxtgUcfhFwr9spFxCI/edit?usp=sharing'

const NAV_ITEMS: { label: string; to: string; kind: 'route' | 'hash' }[] = [
  { label: 'Shows', to: '/shows', kind: 'route' },
  { label: 'Faculty', to: '/faculty', kind: 'route' },
  { label: 'Guiding Principles', to: '/guiding-principles', kind: 'route' },
  { label: 'Alumni', to: '/alumni', kind: 'route' },
  { label: 'Resources', to: '/resources', kind: 'route' },
  { label: 'Guests', to: '/guests', kind: 'route' },
  { label: 'Contact', to: '/#contact', kind: 'hash' },
  { label: 'FAQs', to: '/apply', kind: 'route' },
]

/*
 * The drawer renders through a portal on document.body. Previously it was a
 * `position: absolute` child of the sticky header, which meant it was anchored
 * to that 64px strip rather than the viewport and sat inside the header's
 * stacking context — the floating Calendly badge painted straight over the
 * links. A portal takes it out of both problems.
 */
const Scrim = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.open ? 1 : 0)};
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  /* Above the floating Calendly badge, which injects itself at 9998. */
  z-index: 10000;
`

/* top/bottom rather than height:100vh — on mobile browsers 100vh is the tall
   viewport, so the last item hid behind the address bar. */
const Panel = styled.nav<{ open: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: min(320px, 84vw);
  background: #ffffff;
  box-shadow: -8px 0 28px rgba(0, 0, 0, 0.22);
  transform: translateX(${(props) => (props.open ? '0' : '100%')});
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  transition: transform 0.32s cubic-bezier(0.22, 0.61, 0.36, 1),
    visibility 0.32s ease;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  /* The list is taller than a short viewport once the browser chrome is up, so
     it has to scroll rather than clip. */
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
`

const PanelHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0.5rem 0.5rem 0.25rem;
  background: #ffffff;
  flex-shrink: 0;
`

const Close = styled.button`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  color: ${(props) => props.theme.colors.black};
  cursor: pointer;
  padding: 0;

  &:focus-visible {
    outline: 3px solid #990000;
    outline-offset: -2px;
    border-radius: 4px;
  }
`

/* Tracking is 0.06em, not the old 0.25rem: at a 320px drawer width that much
   letter-spacing pushed "Guiding Principles" onto a second line. */
const Items = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: calc(2rem + env(safe-area-inset-bottom, 0px));

  a {
    display: block;
    padding: 0.85rem 1.5rem;
    font-size: 1.05rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${(props) => props.theme.colors.red};
    text-decoration: none;
    border-bottom: 1px solid #f1f1f1;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover,
    &:focus-visible {
      background-color: ${(props) => props.theme.colors.red};
      color: #ffffff;
      outline: none;
    }
  }
`

const Menu: FC<Prop> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  return createPortal(
    <>
      <Scrim open={open} onClick={onClose} aria-hidden="true" />
      <Panel
        open={open}
        id="mobile-menu"
        aria-label="Mobile navigation"
        aria-hidden={!open}
      >
        <PanelHeader>
          <Close type="button" aria-label="Close navigation menu" onClick={onClose}>
            ×
          </Close>
        </PanelHeader>
        <Items>
          <a
            href={SYLLABUS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
          >
            Syllabus
          </a>
          {NAV_ITEMS.map((item) =>
            item.kind === 'hash' ? (
              <NavHashLink key={item.label} to={item.to} onClick={onClose}>
                {item.label}
              </NavHashLink>
            ) : (
              // Router links, not raw <a href>: the old markup forced a full
              // page reload out of every menu tap.
              <Link key={item.label} to={item.to} onClick={onClose}>
                {item.label}
              </Link>
            )
          )}
        </Items>
      </Panel>
    </>,
    document.body
  )
}

export default Menu
